import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { restoreGanttItemsWithCalendarSync } from "@/lib/deleteLeadWithCalendarCleanup";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { History, RotateCcw, Trash2, Edit, Plus } from "lucide-react";
import { format } from "date-fns";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getCurrentUser } from "@/lib/authUser";

interface Snapshot {
  id: string;
  lead_id: string;
  snapshot_data: any;
  change_type: 'created' | 'updated' | 'deleted';
  created_at: string;
  changed_by: string;
}

interface VersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore?: () => void;
}

export function VersionHistoryDialog({ open, onOpenChange, onRestore }: VersionHistoryDialogProps) {
  
  const { workspace, selectedGroupId } = useWorkspace();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchSnapshots();
    }
  }, [open, workspace, selectedGroupId]);

  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      if (!user) return;

      let query = supabase
        .from("lead_snapshots")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      if (workspace === "groups" && selectedGroupId) {
        query = query.eq("group_id", selectedGroupId);
      } else {
        query = query.eq("user_id", user.id).is("group_id", null);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSnapshots((data || []) as Snapshot[]);
    } catch (error: any) {
      toast.error("Error loading history", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (snapshot: Snapshot) => {
    try {
      setRestoring(snapshot.id);
      const user = await getCurrentUser();
      if (!user) return;

      const leadData = snapshot.snapshot_data;

      if (snapshot.change_type === 'deleted') {
        // Restore deleted lead
        const { error } = await supabase
          .from("leads")
          .insert([{
            id: leadData.id,
            user_id: leadData.user_id,
            group_id: leadData.group_id,
            lead_name: leadData.lead_name,
            company: leadData.company,
            email: leadData.email,
            phone: leadData.phone,
            status: leadData.status,
            priority: leadData.priority,
            value: leadData.value,
            source: leadData.source,
            conversation_notes: leadData.conversation_notes,
            next_action: leadData.next_action,
            next_action_date: leadData.next_action_date,
            custom_fields: leadData.custom_fields,
            last_refreshed_at: leadData.last_refreshed_at,
          }]);

        if (error) throw error;

        // Restore gantt items from the snapshot's lead_gantt_items (if any were saved)
        // and re-sync to Google Calendar
        try {
          const { data: ganttSnapshots } = await supabase
            .from("lead_gantt_items")
            .select("*")
            .eq("lead_id", leadData.id);
          
          // If no gantt items exist (they were deleted with the lead), 
          // check lead_history for gantt_task_deleted entries to find what was deleted
          if (!ganttSnapshots || ganttSnapshots.length === 0) {
            const { data: historyEntries } = await supabase
              .from("lead_history")
              .select("changed_fields")
              .eq("lead_id", leadData.id)
              .eq("action", "gantt_task_deleted")
              .order("created_at", { ascending: false });

            if (historyEntries && historyEntries.length > 0) {
              const itemsToRestore = historyEntries
                .map((h: any) => h.changed_fields?.old)
                .filter(Boolean);

              if (itemsToRestore.length > 0) {
                const groupId = leadData.group_id || selectedGroupId || null;
                await restoreGanttItemsWithCalendarSync(
                  itemsToRestore,
                  leadData.lead_name,
                  { groupId, workspaceName: groupId ? undefined : 'Personal' }
                );
              }
            }
          }
        } catch (restoreErr) {
          console.error("Error restoring gantt items from history:", restoreErr);
        }

        toast.success("Lead restored", { description: "The deleted lead and its timeline events have been restored." });
      } else {
        // Revert to previous state
        const { error } = await supabase
          .from("leads")
          .update({
            lead_name: leadData.lead_name,
            company: leadData.company,
            email: leadData.email,
            phone: leadData.phone,
            status: leadData.status,
            priority: leadData.priority,
            value: leadData.value,
            source: leadData.source,
            conversation_notes: leadData.conversation_notes,
            next_action: leadData.next_action,
            next_action_date: leadData.next_action_date,
            custom_fields: leadData.custom_fields,
            last_refreshed_at: leadData.last_refreshed_at,
          })
          .eq("id", leadData.id);

        if (error) throw error;

        toast.success("Version restored", { description: "The lead has been reverted to this version." });
      }

      onRestore?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Error restoring version", { description: error.message });
    } finally {
      setRestoring(null);
    }
  };

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'created':
        return <Plus className="h-4 w-4" />;
      case 'updated':
        return <Edit className="h-4 w-4" />;
      case 'deleted':
        return <Trash2 className="h-4 w-4" />;
      default:
        return <History className="h-4 w-4" />;
    }
  };

  const getChangeColor = (changeType: string) => {
    switch (changeType) {
      case 'created':
        return "default";
      case 'updated':
        return "secondary";
      case 'deleted':
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History
          </DialogTitle>
          <DialogDescription>
            View and restore previous versions of your leads. All changes are automatically tracked.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : snapshots.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No version history available yet
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {snapshots.map((snapshot) => (
                <div
                  key={snapshot.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={getChangeColor(snapshot.change_type)} className="gap-1">
                          {getChangeIcon(snapshot.change_type)}
                          {snapshot.change_type}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(snapshot.created_at), "MMM d, yyyy 'at' h:mm a")}
                        </span>
                      </div>
                      
                      <div className="text-sm">
                        <div className="font-medium">
                          {snapshot.snapshot_data.lead_name}
                        </div>
                        {snapshot.snapshot_data.company && (
                          <div className="text-muted-foreground">
                            {snapshot.snapshot_data.company}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          Status: {snapshot.snapshot_data.status} | 
                          Priority: {snapshot.snapshot_data.priority}
                        </div>
                      </div>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(snapshot)}
                      disabled={restoring !== null}
                      className="gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      {restoring === snapshot.id ? "Restoring..." : "Restore"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}